import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, Trash2, Users2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner } from "@/components/shared";
import { ConfirmDialog } from "@/components/dialogs";

export default function Demo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.demo();
      setData(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const seed = async () => {
    setSeeding(true);
    try {
      await api.seedDemo();
      toast.success("Demo profiles topped up to 55 men + 55 women");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeeding(false);
    }
  };

  const wipe = async () => {
    await api.wipeDemo();
    toast.success("All demo profiles deleted");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Demo Profiles"
        subtitle="Sample profiles used for testing. Wipe them before public launch."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={seed} disabled={seeding} data-testid="seed-btn">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Seed demo
            </Button>
            <Button variant="destructive" onClick={() => setWipeOpen(true)} data-testid="wipe-btn">
              <Trash2 className="h-4 w-4" /> Wipe all
            </Button>
          </div>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Stat label="Total demo profiles" value={data?.total ?? 0} icon={Sparkles} highlight />
            <Stat label="Men" value={data?.men ?? 0} icon={Users2} />
            <Stat label="Women" value={data?.women ?? 0} icon={Users2} />
          </div>

          <h3 className="font-display text-lg mb-3">Sample profiles</h3>
          {data?.sample?.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="demo-grid">
              {data.sample.map((p) => (
                <div key={p.user_id} className="rounded-2xl bg-card ring-1 ring-border/70 p-3 text-center" data-testid={`demo-${p.user_id}`}>
                  <Avatar src={p.photo} name={p.name} size={72} className="mx-auto" />
                  <div className="font-medium mt-2 truncate">
                    {p.name}{p.age ? `, ${p.age}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[p.city, p.country].filter(Boolean).join(", ")}
                  </div>
                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <Tag>{p.gender}</Tag>
                    <Tag tone={p.status === "online" ? "green" : "muted"}>{p.status}</Tag>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No demo profiles. Click "Seed demo" to create some.</p>
          )}
        </>
      )}

      <ConfirmDialog
        open={wipeOpen}
        onOpenChange={setWipeOpen}
        title="Wipe all demo profiles?"
        description="This permanently deletes every demo/sample profile. Use this right before your public launch. This cannot be undone."
        confirmText="Wipe all demo profiles"
        destructive
        onConfirm={wipe}
      />
    </div>
  );
}

function Stat({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`rounded-2xl p-5 ring-1 ${highlight ? "bg-gradient-to-br from-primary to-[hsl(8_55%_44%)] text-primary-foreground ring-primary/40" : "bg-card ring-border/70"}`}>
      <div className="flex items-center justify-between">
        <span className={`text-sm ${highlight ? "opacity-80" : "text-muted-foreground"}`}>{label}</span>
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div className="font-display text-4xl mt-3">{value}</div>
    </div>
  );
}
